/**
 * "Can this video be saved to a device, how, and what should the file be
 * called?"
 *
 * Bobbie, 2026-08-28: *"The other thing we need to add to all video is a
 * download button. Parents are asking to be able to save the videos which we
 * are ok with."*
 *
 * Watching a video and SAVING it are different problems, and the difference
 * is where the bytes live:
 *
 *   - OUR FIREBASE STORAGE BUCKET. Fetchable from the browser (the message
 *     attachment save has fetched from it for months), so the bytes can be
 *     pulled into a Blob and handed to the iOS share sheet, which is the only
 *     road a web app has to the camera roll - and the camera roll is what a
 *     dance parent means by "save it".
 *   - R2. The bucket is private (DECISIONS.md, "Cloudflare R2 is the video
 *     home"), so there is no fetchable URL at all until the server mints a
 *     presigned one. It can mint that URL carrying a
 *     `Content-Disposition: attachment`, which makes the browser save the file
 *     rather than play it - with no bytes through this app's memory, which for
 *     a full-length recital video is the difference between a download and a
 *     dead tab.
 *   - EVERYTHING ELSE - a pasted YouTube, Drive or Vimeo link. The browser
 *     cannot fetch those cross-origin and their hosts do not hand out files.
 *     There is no honest download here, so this file says so and the screen
 *     draws no Save button rather than one that quietly opens YouTube. The
 *     "Open in a new tab" link those entries already have is unchanged.
 *
 * WHY THE BUCKET IS CHECKED, not just the host: every Firebase project in the
 * world shares firebasestorage.googleapis.com, so a hand-pasted link to a
 * FOREIGN project's bucket would otherwise be fetched and saved wearing an LDG
 * dance's name. Same reasoning, same shape, as isDownloadableMusicUrl in
 * lib/musicDownload.ts and safeHubStorageDownloadUrl in lib/saveAttachment.ts.
 *
 * NOTHING in this file may import a local module: Node's type stripping does
 * not run the bundler's resolver, and tests/video-download.test.mjs runs this
 * file directly. It imports nothing.
 */

const FIREBASE_STORAGE_HOST = 'firebasestorage.googleapis.com';

/**
 * The Hub's own Storage buckets. Firebase renamed new default buckets from
 * appspot.com to firebasestorage.app, and real Hub media legitimately lives
 * in either form - but never in another Firebase project's bucket. Mirrors
 * HUB_STORAGE_BUCKETS in lib/saveAttachment.ts, which cannot be imported here
 * (see the header's no-imports rule); tests/video-download.test.mjs pins the
 * two lists against each other so they cannot drift.
 */
export const HUB_STORAGE_BUCKET_NAMES = [
  'ldg-helper.firebasestorage.app',
  'ldg-helper.appspot.com',
];

/**
 * The browser holds every downloaded byte in memory before it can hand the
 * file to the share sheet or the download machinery, and a phone tab dies well
 * before the address space does. Past this, saying so is honest; buffering
 * anyway and dying is not. Same shape of budget, for the same reason, as
 * MUSIC_FILE_MAX_BYTES.
 *
 * PR #809 review round 9 (Codex): the reason string used to point at "the
 * link next to it," which assumed every caller renders one. PostCard.tsx's
 * legacy video block did not - a dead end, on a screen this function has no
 * way to see. This file cannot know what, if anything, a caller draws beside
 * its button (a permanent link, a failure-only escape hatch, or nothing yet),
 * so the wording below says only what happened, never what to do about it.
 * A caller that has a route out shows its own control when this fires -
 * SaveVideoButton's onCannotSave (and every screen that wires it) exists for
 * exactly that.
 *
 * Only ever applied on the FETCH route. The R2 route never buffers - the
 * browser's own download machinery streams it - so a full recital video saves
 * fine there regardless of this number.
 */
export const VIDEO_FETCH_MAX_BYTES = 512 * 1024 * 1024;

export interface DownloadableLink {
  url: string;
  storage?: 'r2';
  r2Key?: string;
}

export type VideoDownloadPlan =
  /** Presign a `Content-Disposition: attachment` URL and let the browser save it. */
  | { route: 'r2'; r2Key: string }
  /** Fetch the bytes from our own bucket, then share-sheet or download them. */
  | { route: 'fetch'; url: string }
  /** No honest download exists. `reason` is the sentence a screen may show. */
  | { route: 'none'; reason: string };

const OFFSITE_REASON = 'This one lives on another site, so it opens there instead of saving.';

/**
 * How (or whether) this link can be saved. Pure: it decides from the link
 * alone and never touches the network, so a screen can ask before it draws a
 * button rather than after a parent has already tapped one.
 */
export function videoDownloadPlan(link: DownloadableLink): VideoDownloadPlan {
  if (link.storage === 'r2') {
    return link.r2Key
      ? { route: 'r2', r2Key: link.r2Key }
      : { route: 'none', reason: 'This video is missing its storage key.' };
  }
  let parsed: URL;
  try {
    parsed = new URL(link.url);
  } catch {
    return { route: 'none', reason: OFFSITE_REASON };
  }
  if (parsed.protocol !== 'https:' || parsed.hostname !== FIREBASE_STORAGE_HOST) {
    return { route: 'none', reason: OFFSITE_REASON };
  }
  const ours = HUB_STORAGE_BUCKET_NAMES.some((name) => (
    parsed.pathname.startsWith(`/v0/b/${name}/o/`)
  ));
  return ours ? { route: 'fetch', url: parsed.toString() } : { route: 'none', reason: OFFSITE_REASON };
}

/** True when a screen should draw a Save control at all. */
export function canSaveVideo(link: DownloadableLink): boolean {
  return videoDownloadPlan(link).route !== 'none';
}

export type VideoFetchDecision = { action: 'fetch' } | { action: 'skip'; reason: string };

/**
 * The per-file budget gate, checked against the declared Content-Length before
 * buffering and again against the real byte count after, so a server that
 * declines to state a length cannot smuggle an oversized file past it.
 */
export function videoFetchDecision(sizeBytes: number | null): VideoFetchDecision {
  if (sizeBytes != null && sizeBytes > VIDEO_FETCH_MAX_BYTES) {
    return {
      action: 'skip',
      reason: 'This file is too big to save from here.',
    };
  }
  return { action: 'fetch' };
}

/**
 * The one extension list a saved video may carry. Deliberately short: the name
 * is built from a class name and a studio-written label, both of which live in
 * a Firestore document staff can edit, and a saved file inherits its behaviour
 * from its extension. Anything unrecognised becomes .mp4 rather than being
 * trusted through.
 */
const VIDEO_EXTENSION_BY_TYPE: Record<string, string> = {
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
  'video/webm': 'webm',
  'video/3gpp': '3gp',
  'video/x-m4v': 'm4v',
};

/**
 * The same list for MUSIC (Bobbie, 2026-08-29, asked whether the Save control
 * should cover music too: *"Yes, download a music too"*).
 *
 * Deliberately the SAME EIGHT the studio can already upload: this mirrors the
 * extension map in components/messageAttachments/MessageAttachmentComposer.tsx
 * and lib/tasks.ts rather than inventing a second opinion about what a music
 * file is. If those grow, this grows with them.
 */
const AUDIO_EXTENSION_BY_TYPE: Record<string, string> = {
  'audio/mpeg': 'mp3',
  'audio/mp4': 'm4a',
  'audio/x-m4a': 'm4a',
  'audio/wav': 'wav',
  'audio/x-wav': 'wav',
  'audio/aac': 'aac',
  'audio/ogg': 'ogg',
  'audio/flac': 'flac',
  'audio/aiff': 'aiff',
  'audio/x-aiff': 'aiff',
};

const KEEPABLE_VIDEO_EXTENSIONS = new Set(['mp4', 'mov', 'm4v', 'webm', '3gp']);
const KEEPABLE_AUDIO_EXTENSIONS = new Set(['mp3', 'm4a', 'wav', 'aac', 'ogg', 'flac', 'aif', 'aiff']);

/**
 * Every extension a saved file may carry, video or music. The name crosses
 * into a response header and a saved file inherits its behaviour from its
 * extension, so this stays a short allowlist and never a blocklist.
 */
export const KEEPABLE_MEDIA_EXTENSIONS = new Set([
  ...KEEPABLE_VIDEO_EXTENSIONS,
  ...KEEPABLE_AUDIO_EXTENSIONS,
]);

/**
 * The strict shape the SERVER will accept for a download filename, mirrored
 * here so a screen never sends a name that gets refused. The name crosses into
 * a `Content-Disposition` response header, so the rule is characters-IN rather
 * than characters-out: letters, digits, spaces and a little punctuation,
 * nothing else, ever.
 *
 * netlify/functions/r2Playback.mjs applies the same rule as the authority.
 * This is the client half, so a refusal is impossible rather than merely
 * handled, and tests/video-download.test.mjs pins the two together.
 */
const SAFE_DOWNLOAD_NAME = /^[A-Za-z0-9 ._()'&,+-]{1,120}$/;

function extensionOf(name: string): string {
  return (/\.([a-z0-9]{2,4})$/i.exec(name)?.[1] ?? '').toLowerCase();
}

export function isSafeDownloadName(name: string): boolean {
  return SAFE_DOWNLOAD_NAME.test(name) && KEEPABLE_MEDIA_EXTENSIONS.has(extensionOf(name));
}

/**
 * "Glee 1 - Full run with corrections.mp4".
 *
 * The dance name leads, because a phone's camera roll is where these land and
 * "video.mp4" ten times over is not a saved video, it is a puzzle. Falls back
 * through the label alone, then a plain noun, so a nameless link still saves
 * as something a person can find again.
 *
 * The result always satisfies isSafeDownloadName, so the same string is good
 * for the share sheet, for `<a download>`, and for the server's header.
 */
export function videoFileName(input: {
  className?: string | null;
  label?: string | null;
  contentType?: string | null;
  /**
   * The name the file was uploaded under, when the studio recorded one. This
   * is the FIRST place the extension is read from, ahead of the content type
   * and well ahead of the lane, because it is the only field the music upload
   * path actually stores: MediaPanel's uploadMusicFile writes `fileName` and
   * no `contentType` at all, so without this every class-uploaded .wav, .m4a,
   * .flac and .aiff would save as ".mp3" and only mp3s would be right.
   *
   * Same order lib/musicDownload.ts's extensionForMusicLink already uses for
   * the staff zip of these very files, so the two features name one file the
   * same way. Its rule, and the reason: "never guesses mp3, since a wrongly
   * labeled extension on a real file is worse than an honest unknown one".
   */
  fileName?: string | null;
  /**
   * Which lane this came from. The LAST resort, when neither the stored file
   * name nor a content type says anything: a music link that lands as ".mp4"
   * is wrong about itself, and some players refuse it outright.
   */
  kind?: 'music' | 'video';
}): string {
  const clean = (value: string | null | undefined) => (value ?? '')
    // Everything outside the allowlist becomes a SPACE rather than vanishing,
    // so "Ava/Leo duet" does not silently become "AvaLeo duet".
    .replace(/[^A-Za-z0-9 ._()'&,+-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const className = clean(input.className);
  const label = clean(input.label);
  const joined = [className, label].filter(Boolean).join(' - ');

  const music = input.kind === 'music';
  const type = (input.contentType ?? '').toLowerCase().split(';')[0].trim();
  // THE ORDER MATTERS, and it is the same order the staff zip already uses.
  // The stored file name first (the only thing a music upload records), then a
  // recorded content type, then the lane. A mislabelled lane must never
  // override something the studio actually wrote down.
  const stored = extensionOf(input.fileName ?? '');
  const storedExtension = KEEPABLE_MEDIA_EXTENSIONS.has(stored) ? stored : undefined;
  const typeExtension = VIDEO_EXTENSION_BY_TYPE[type] ?? AUDIO_EXTENSION_BY_TYPE[type];

  // A LABEL that already ends in a real media extension: "run 3.mov" must not
  // become "run 3.mov.mp4", so its extension is stripped off the stem and can
  // still be the answer - but only when nothing better exists.
  //
  // IT RANKS BELOW THE STORED FACTS, which is the whole precedence and was
  // wrong when music first landed. A label is TYPED BY A PERSON: staff upload
  // "song.wav" and title it "Final cut.mp3", and the label used to win, so the
  // WAV downloaded as an .mp3 - exactly the rule the stored-name-first change
  // had just been written to establish, undone two lines later. Same for a
  // recorded content type, which a label was also outranking.
  const existing = extensionOf(joined);
  const labelExtension = KEEPABLE_MEDIA_EXTENSIONS.has(existing) ? existing : undefined;
  const stem = labelExtension
    ? joined.slice(0, joined.length - existing.length - 1).trim()
    : joined;
  // Stored name, then recorded type, then the person-typed label, then the
  // lane. Every step allowlisted.
  const finalExtension = storedExtension
    ?? typeExtension
    ?? labelExtension
    ?? (music ? 'mp3' : 'mp4');

  // 120 is the server's ceiling; leave room for the extension and the dot.
  const fallbackStem = music ? 'Studio music' : 'Studio video';
  const capped = (stem || fallbackStem).slice(0, 119 - finalExtension.length).trim() || fallbackStem;
  const name = `${capped}.${finalExtension}`;
  return isSafeDownloadName(name) ? name : (music ? 'Studio music.mp3' : 'Studio video.mp4');
}
